import { InlineNotification } from '@carbon/react';
import { unvaluedReasonSentence } from 'components/unvalued-reason';
import type {
  CommissionRates,
  TearsheetOutputs,
} from 'types/packages-api/slot-valuation-inputs';
import { PendingValue, ValueTable } from './tearsheet-cells';
import { TearsheetLink } from './tearsheet-link';
import { type MetaByNode } from './tearsheet-sections';
import {
  COMMISSION_GROUPS,
  commissionGroupCaption,
  commissionGroupHeading,
  EMPTY,
  FALLBACK_OUTPUT_UNITS,
  formatOutputValue,
  outputLabel,
  outputRows,
  type OutputRowState,
} from './utils';
import type { ReasonLink } from 'components/unvalued-reason';
import styles from './slot-tearsheet.module.scss';

// The default for a reason with no fix, one array for every render.
const NO_LINKS: readonly ReasonLink[] = [];

/**
 * The Calculated result section — the six NPV outputs, the cashflow-series length,
 * and (when the slot is legitimately unvalued) the reason. A present
 * `unvaluedReason` on a 200 is NOT an error (contract) — it renders as an info
 * notice, with the reason's definition stated in full (a notice is read, not hovered),
 * followed by a link to where each of its causes gets fixed, for those that have one (INS-12687).
 *
 * @param {object} props - The tearsheet outputs.
 * @return {JSX.Element} The Calculated result section.
 */
export function CalculatedResult({
  outputs,
  meta,
  metaResolved,
  metaFailed,
  formulaFor,
  commissionRates,
  fixLinks = NO_LINKS,
}: {
  outputs: TearsheetOutputs;
  meta: MetaByNode;
  metaResolved: boolean;
  metaFailed: boolean;
  formulaFor: (key: string) => string | null;
  commissionRates?: CommissionRates | null;
  /** Where each cause of the unvalued reason gets fixed (see `reasonFixLinks`); empty → no links. */
  fixLinks?: readonly ReasonLink[];
}) {
  // AC 13's rows come from the CRITERION, not from whatever keys the payload carries — see
  // outputRows. A column the calculator never wrote used to produce no row at all.
  const rows = outputRows(outputs.values, Boolean(outputs.unvaluedReason));
  const months = outputs.cashflowSeries?.rows.length ?? 0;
  // ── UAT 6 ────────────────────────────────────────────────────────────────────────────
  // The six NPVs split into three commission groups; everything else (irr, payout_month,
  // and any column a future calculator adds) keeps its flat table below. Partitioned from
  // COMMISSION_GROUPS rather than by key prefix, so an unrecognised key cannot silently
  // vanish into a group it does not belong to — it falls through to `ungrouped`.
  const grouped = new Set<string>(
    COMMISSION_GROUPS.flatMap((g) => [...g.keys])
  );
  const byKey = new Map(rows.map((row) => [row.key, row]));
  const ungrouped = rows.filter((row) => !grouped.has(row.key));
  return (
    <section className={styles.zoneSection}>
      <div className={styles.zoneHeader}>
        <h4 className={styles.zoneTitle}>Calculated result</h4>
      </div>
      {outputs.unvaluedReason && (
        <>
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            title="Slot unvalued"
            subtitle={unvaluedReasonSentence(outputs.unvaluedReason, outputs)}
            className={styles.notice}
          />
          {/* The route to the fix is a SIBLING of the notice, not in its subtitle:
              Carbon's InlineNotification throws on any interactive descendant (see
              expense-coverage-notification.tsx). */}
          {fixLinks.map((link) => (
            <p key={link.href} className={styles.noticeFix}>
              <TearsheetLink href={link.href}>{link.label}</TearsheetLink>
            </p>
          ))}
        </>
      )}
      {/* ── UAT 6: ONE GROUP PER COMMISSION, HEADED WITH ITS RATE ──────────────────
          The pairs looked duplicated because nothing on the page said what separated
          them. A group with no rows at all is not rendered; a group whose rate is
          unknown still renders, saying so, because the ROWS are real either way. */}
      {COMMISSION_GROUPS.map((group) => {
        const groupRows = group.keys
          .map((key) => byKey.get(key))
          .filter((row): row is { key: string; state: OutputRowState } =>
            Boolean(row)
          );
        if (groupRows.length === 0) {
          return null;
        }
        const rate = commissionRates?.[group.kind];
        const caption = commissionGroupCaption(rate);
        return (
          <div key={group.kind} className={styles.expenseGroup}>
            <h5 className={styles.expenseGroupTitle}>
              {commissionGroupHeading(group.label, rate)}
            </h5>
            {caption && <p className={styles.blockCaption}>{caption}</p>}
            <ValueTable head="Output">
              {groupRows.map(({ key, state }) => (
                <OutputRow
                  key={key}
                  outputKey={key}
                  state={state}
                  outputs={outputs}
                  meta={meta}
                  metaResolved={metaResolved}
                  metaFailed={metaFailed}
                  formulaFor={formulaFor}
                />
              ))}
            </ValueTable>
          </div>
        );
      })}
      <ValueTable head="Output">
        {ungrouped.map(({ key, state }) => (
          <OutputRow
            key={key}
            outputKey={key}
            state={state}
            outputs={outputs}
            meta={meta}
            metaResolved={metaResolved}
            metaFailed={metaFailed}
            formulaFor={formulaFor}
          />
        ))}
        {outputs.cashflowSeries && (
          <tr>
            <td className={styles.paramName}>Cashflow series</td>
            <td className={styles.paramValue}>{months} mo</td>
          </tr>
        )}
      </ValueTable>
    </section>
  );
}

/**
 * One Calculated-result row. Extracted verbatim from CalculatedResult so the three
 * commission groups and the ungrouped table render the SAME row, rather than three copies
 * of it that could drift.
 *
 * @param {object} props - The output key, its state, and the maps that render it.
 * @return {JSX.Element} The row.
 */
export function OutputRow({
  outputKey: key,
  state,
  outputs,
  meta,
  metaResolved,
  metaFailed,
  formulaFor,
}: {
  outputKey: string;
  state: OutputRowState;
  outputs: TearsheetOutputs;
  meta: MetaByNode;
  metaResolved: boolean;
  metaFailed: boolean;
  formulaFor: (key: string) => string | null;
}) {
  return (
    <>
      {
        <tr key={key}>
          <td className={styles.paramName}>
            {outputLabel(key, meta[key]?.label)}
            {/* The third state, quiet and factual, in the same register as the
                  provenance caption. NOT a value and NOT a zero: saying the well has no
                  IRR when the truth is that nobody computed one is the failure this
                  distinction exists to prevent. */}
            {state === 'not_computed' && (
              <span className={styles.outputNotComputed}>
                Not computed by this run
              </span>
            )}
            {/* AC 5. Two of the eight carry a formula — `irr` (2 lines) and
                  `payout_month` (5) — and suppressing their zone rows above would have
                  DELETED those explanations from the page. Read from `slotInputs`, the
                  same source the zone tables use, so it paints on the first response and
                  cannot disagree with them (AC 14). The six NPVs declare none. */}
            {formulaFor(key) && (
              <span className={styles.formula}>{formulaFor(key)}</span>
            )}
          </td>
          <td className={styles.paramValue}>
            {state === 'value' &&
            typeof outputs.values?.[key] === 'number' &&
            meta[key]?.unit === undefined &&
            !metaResolved &&
            !metaFailed ? (
              // Only while the map may still ARRIVE. On a terminal failure (metaFailed) the
              // unit will never come, so render the number through the branch below — using
              // FALLBACK_OUTPUT_UNITS so `irr` still scales — rather than the `…` placeholder
              // forever.
              <PendingValue />
            ) : state === 'value' ? (
              // The 8-decimal rendering is gone with the duplicate zone row, so the
              // unrounded figure lives on the title. The displayed number is unchanged.
              //
              // FALLBACK on the unit: when the map failed (metaFailed) meta[key] is absent, so
              // a raw `irr` fraction would take format0 and an 18.52% return would render as
              // `0`. FALLBACK_OUTPUT_UNITS supplies `irr`'s `fraction` so it scales correctly;
              // the six NPVs need no unit and payout_month is a date/count.
              <span title={String(outputs.values?.[key])}>
                {formatOutputValue(
                  outputs.values?.[key],
                  meta[key]?.unit ?? FALLBACK_OUTPUT_UNITS[key]
                )}
              </span>
            ) : (
              <span
                className={styles.nodeValueFaded}
                title={
                  state === 'no_value'
                    ? 'No value for this well'
                    : 'This calculation produced no such column'
                }
              >
                {EMPTY}
              </span>
            )}
          </td>
        </tr>
      }
    </>
  );
}
